import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminDomainsAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Globe, CheckCircle, XCircle, Clock, ArrowClockwise, Plus, Copy, Info
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

const STATUS_MAP = {
  verified: { color: '#22C55E', icon: CheckCircle, label: 'Verified' },
  not_started: { color: '#F59E0B', icon: Clock, label: 'Pending' },
  pending: { color: '#F59E0B', icon: Clock, label: 'Pending' },
  failed: { color: '#EF4444', icon: XCircle, label: 'Failed' },
  temporary_failure: { color: '#F59E0B', icon: Clock, label: 'Retrying' },
};

export default function AdminDomainsPage() {
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainDetail, setDomainDetail] = useState(null);
  const [newDomain, setNewDomain] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiKeyRestricted, setApiKeyRestricted] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDomains(); }, []);

  const loadDomains = async () => {
    setLoading(true);
    try {
      const { data } = await adminDomainsAPI.list();
      setDomains(Array.isArray(data) ? data : []);
      setApiKeyRestricted(false);
    } catch (err) {
      const msg = formatApiError(err);
      if (err.response?.status === 403 || msg.includes('restricted')) {
        setApiKeyRestricted(true);
      } else {
        toast.error(msg);
      }
    }
    setLoading(false);
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    setAddingDomain(true);
    try {
      await adminDomainsAPI.create(newDomain.trim());
      toast.success(`Domain "${newDomain}" registered! Add the DNS records shown below.`);
      setNewDomain('');
      loadDomains();
    } catch (err) { toast.error(formatApiError(err)); }
    setAddingDomain(false);
  };

  const viewDomain = async (domainId) => {
    setSelectedDomain(domainId);
    try {
      const { data } = await adminDomainsAPI.get(domainId);
      setDomainDetail(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const triggerVerify = async (domainId) => {
    try {
      await adminDomainsAPI.verify(domainId);
      toast.success('Verification triggered. DNS propagation may take a few minutes.');
      if (selectedDomain === domainId) viewDomain(domainId);
      loadDomains();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getStatus = (s) => STATUS_MAP[s] || STATUS_MAP.pending;

  return (
    <div className="min-h-screen bg-[#111111]" data-testid="admin-domains-page">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard"><img src={LOGO_URL} alt="Divispend" className="h-10 w-auto" /></Link>
            <span className="text-[#666666]">/</span>
            <span className="text-[#22C55E] font-semibold text-sm">Admin</span>
            <span className="text-[#666666]">/</span>
            <span className="text-white font-semibold text-sm">Domain Verification</span>
          </div>
          <Link to="/dashboard">
            <Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm text-sm">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* Instructions Card */}
        <Card className="bg-[#0D1F17] border-[#22C55E]/20 rounded-lg">
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-[#22C55E] shrink-0 mt-0.5" />
            <div className="text-sm text-[#999999] space-y-1">
              <p className="text-white font-medium">How to verify your domain</p>
              <p>1. Add your domain below (e.g., <code className="text-[#22C55E] bg-[#111111] px-1 rounded">divispend.com</code>)</p>
              <p>2. Copy the DNS records and add them in your domain registrar (GoDaddy, Namecheap, etc.)</p>
              <p>3. Wait for DNS propagation (usually 5-30 minutes, up to 72 hours)</p>
              <p>4. Click "Verify" to check if records are detected</p>
              <p>5. Once verified, update the <code className="text-[#22C55E] bg-[#111111] px-1 rounded">SENDER_EMAIL</code> in backend .env to use your domain</p>
            </div>
          </CardContent>
        </Card>

        {/* API Key Restricted Warning */}
        {apiKeyRestricted && (
          <Card className="bg-[#1F1209] border-[#F59E0B]/20 rounded-lg" data-testid="api-key-warning">
            <CardContent className="p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
              <div className="text-sm text-[#999999] space-y-1">
                <p className="text-[#F59E0B] font-medium">API Key Upgrade Required</p>
                <p>Your current Resend API key can only send emails. To manage domains, you need a <span className="text-white font-medium">Full Access</span> API key.</p>
                <p>1. Go to <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-[#22C55E] hover:underline">resend.com/api-keys</a></p>
                <p>2. Create a new API key with <span className="text-white font-medium">"Full access"</span> permission</p>
                <p>3. Update the <code className="text-[#22C55E] bg-[#111111] px-1 rounded">RESEND_API_KEY</code> in backend .env and restart</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add Domain */}
        <Card className="bg-[#1A1A1A] border-[#222222] rounded-lg">
          <CardHeader className="border-b border-[#222222] py-3 px-4">
            <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
              <Globe className="h-4 w-4 text-[#22C55E]" /> Register Domain
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex gap-3">
              <Input
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="divispend.com"
                className="bg-[#111111] border-[#333333] text-white rounded-lg flex-1"
                data-testid="domain-input"
              />
              <Button
                onClick={handleAddDomain}
                disabled={addingDomain || !newDomain.trim()}
                className="bg-[#22C55E] hover:bg-[#1EA34B] text-black font-semibold rounded-lg shrink-0"
                data-testid="add-domain-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> {addingDomain ? 'Adding...' : 'Add Domain'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Domain List */}
        <Card className="bg-[#1A1A1A] border-[#222222] rounded-lg">
          <CardHeader className="border-b border-[#222222] py-3 px-4">
            <CardTitle className="text-sm font-semibold text-white">Registered Domains</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-[#666666]">Loading domains...</div>
            ) : domains.length === 0 ? (
              <div className="p-8 text-center text-[#666666]">No domains registered yet. Add one above.</div>
            ) : (
              <div className="divide-y divide-[#222222]">
                {domains.map(d => {
                  const st = getStatus(d.status);
                  const StIcon = st.icon;
                  return (
                    <div key={d.id} className="px-4 py-3 flex items-center justify-between hover:bg-[#151515] transition-colors" data-testid={`domain-${d.id}`}>
                      <div className="flex items-center gap-3">
                        <Globe className="h-4 w-4 text-[#666666]" />
                        <div>
                          <p className="text-sm font-medium text-white">{d.name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <StIcon className="h-3 w-3" style={{ color: st.color }} weight="fill" />
                            <span className="text-[10px] font-semibold" style={{ color: st.color }}>{st.label}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => viewDomain(d.id)} className="text-[#999999] hover:text-white text-xs h-8" data-testid={`view-domain-${d.id}`}>
                          DNS Records
                        </Button>
                        <Button size="sm" onClick={() => triggerVerify(d.id)} className="bg-[#22C55E]/10 text-[#22C55E] hover:bg-[#22C55E]/20 text-xs h-8 rounded-lg" data-testid={`verify-domain-${d.id}`}>
                          <ArrowClockwise className="h-3 w-3 mr-1" /> Verify
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Domain Detail / DNS Records */}
        {domainDetail && (
          <Card className="bg-[#1A1A1A] border-[#222222] rounded-lg" data-testid="domain-detail">
            <CardHeader className="border-b border-[#222222] py-3 px-4">
              <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
                DNS Records for <span className="text-[#22C55E]">{domainDetail.name}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <p className="text-xs text-[#999999] mb-4">Add these records to your DNS provider (GoDaddy). Copy each value exactly.</p>
              <div className="space-y-3">
                {(domainDetail.records || []).map((rec) => {
                  const recSt = getStatus(rec.status);
                  const RecIcon = recSt.icon;
                  return (
                    <div key={`${rec.type || rec.record}-${rec.name}`} className="bg-[#111111] border border-[#222222] rounded-lg p-3" data-testid={`dns-record-${rec.name}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold bg-[#333333] text-white px-2 py-0.5 rounded">{rec.record || rec.type}</span>
                          <span className="text-xs text-[#666666]">{rec.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <RecIcon className="h-3 w-3" style={{ color: recSt.color }} weight="fill" />
                          <span className="text-[10px]" style={{ color: recSt.color }}>{recSt.label}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-[#999999] bg-[#0A0A0A] px-2 py-1 rounded flex-1 overflow-x-auto whitespace-nowrap">
                          {rec.value}
                        </code>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(rec.value)} className="text-[#999999] hover:text-white h-7 w-7 p-0 shrink-0" title="Copy">
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      {rec.ttl && <p className="text-[10px] text-[#444444] mt-1">TTL: {rec.ttl} {rec.priority !== undefined ? `| Priority: ${rec.priority}` : ''}</p>}
                    </div>
                  );
                })}
              </div>
              {domainDetail.status === 'verified' && (
                <div className="mt-4 bg-[#0D1F17] border border-[#22C55E]/20 rounded-lg p-3">
                  <p className="text-sm text-[#22C55E] font-medium">Domain verified!</p>
                  <p className="text-xs text-[#999999] mt-1">Update your <code className="text-[#22C55E]">SENDER_EMAIL</code> to <code className="text-[#22C55E]">Divispend &lt;noreply@{domainDetail.name}&gt;</code></p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* GoDaddy Instructions */}
        <Card className="bg-[#1A1A1A] border-[#222222] rounded-lg">
          <CardHeader className="border-b border-[#222222] py-3 px-4">
            <CardTitle className="text-sm font-semibold text-white">GoDaddy DNS Setup Guide</CardTitle>
          </CardHeader>
          <CardContent className="p-4 text-sm text-[#999999] space-y-2">
            <p><span className="text-white font-medium">Step 1:</span> Log in to <a href="https://dcc.godaddy.com" target="_blank" rel="noreferrer" className="text-[#22C55E] hover:underline">GoDaddy Domain Control Center</a></p>
            <p><span className="text-white font-medium">Step 2:</span> Select your domain (<span className="text-[#22C55E]">divispend.com</span>) and click "DNS" or "Manage DNS"</p>
            <p><span className="text-white font-medium">Step 3:</span> For each DNS record above, click "Add Record":</p>
            <ul className="list-disc list-inside pl-4 space-y-1">
              <li><span className="text-white">TXT records</span> — Type: TXT, Host: the name field, Value: the value field</li>
              <li><span className="text-white">CNAME records</span> — Type: CNAME, Host: the name field, Value: the value field</li>
              <li><span className="text-white">MX records</span> — Type: MX, Host: the name field, Value: the value field, Priority: as shown</li>
            </ul>
            <p><span className="text-white font-medium">Step 4:</span> Save changes and wait 5-30 minutes for propagation</p>
            <p><span className="text-white font-medium">Step 5:</span> Come back here and click "Verify" on your domain</p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
